/**
 * heroNarrative — picks the most "actionable" mover out of the
 * 5 health-score quarter deltas and renders a one-liner that
 * nudges the user into starting a new business case.
 *
 * Priority order (highest = most prompted):
 *   1. Biggest absolute decliner among LEI / PE / ROE / KPI
 *      (ROI is intentionally excluded — it's an *output* metric;
 *      a drop there should already be implied by upstream drivers,
 *      and "model an intervention to fix ROI" is too vague.)
 *   2. Biggest absolute gainer (positive movement = bottle-the-recipe
 *      framing)
 *   3. Fallback to the static data-counts blurb when there's no
 *      history yet.
 *
 * The CTA suffix ("model an intervention →" / "bottle the recipe →"
 * / etc.) is *not* baked in here — the BCB hero fetches a Thompson-
 * sampled variant from `/analytics/hero-narrative-pick` and joins
 * `prefix + " — " + suffix`. That keeps the bandit's catalog in one
 * place (server) and lets the frontend stay catalog-agnostic.
 *
 * Returned object:
 *   { prefix, headline, kind, metricKey, deltaText }
 *
 * `prefix` is everything *before* the em-dash CTA (e.g. "Your ROE
 * dropped 8.1 pts last quarter"). `headline` is a sensible default
 * (prefix + a generic CTA suffix) used as a graceful fallback when
 * the variant-pick endpoint fails.
 */

const METRIC_LABELS = {
  lei: 'LEI',
  pe: 'Performance Effectiveness',
  roe: 'Return on Effort',
  kpi: 'KPI Impact',
  roi: 'ROI',
};

const NUDGE_ELIGIBLE = ['lei', 'pe', 'roe', 'kpi'];

const _fmtPct = (delta) => {
  if (delta == null || Number.isNaN(delta)) return '—';
  const v = Number(delta).toFixed(1);
  return `${v} pts`;
};

const DEFAULT_SUFFIX = {
  decline: 'model an intervention →',
  gain: 'bottle the recipe in a new case →',
  fallback: 'start modelling →',
};

export function buildBcbHeroNarrative({ healthSnapshot, roeContext }) {
  const fallbackPrefix =
    `Inputs draw from your live ROE feed — ` +
    `${roeContext?.data_counts?.l2 || 0} L2 · ` +
    `${roeContext?.data_counts?.motivation || 0} surveys · ` +
    `${roeContext?.data_counts?.l3 || 0} L3 obs.`;
  const fallback = {
    prefix: fallbackPrefix,
    headline: `${fallbackPrefix} ${DEFAULT_SUFFIX.fallback}`,
    kind: 'fallback',
    metricKey: null,
    deltaText: null,
  };

  const metrics = healthSnapshot?.metrics;
  if (!metrics || !healthSnapshot?.has_history) return fallback;

  // Collect candidates with a usable delta
  const candidates = NUDGE_ELIGIBLE
    .map((k) => ({ key: k, ...(metrics[k] || {}) }))
    .filter((m) => m.delta != null && m.direction && m.direction !== 'flat');

  if (candidates.length === 0) return fallback;

  // 1) biggest decliner first
  const decliners = candidates.filter((m) => m.direction === 'down');
  if (decliners.length > 0) {
    decliners.sort((a, b) => Math.abs(b.delta) - Math.abs(a.delta));
    const top = decliners[0];
    const label = METRIC_LABELS[top.key] || top.key.toUpperCase();
    const prefix = `Your ${label} dropped ${_fmtPct(Math.abs(top.delta))} last quarter`;
    return {
      prefix,
      headline: `${prefix} — ${DEFAULT_SUFFIX.decline}`,
      kind: 'decline',
      metricKey: top.key,
      deltaText: _fmtPct(Math.abs(top.delta)),
    };
  }

  // 2) biggest gainer
  candidates.sort((a, b) => Math.abs(b.delta) - Math.abs(a.delta));
  const top = candidates[0];
  const label = METRIC_LABELS[top.key] || top.key.toUpperCase();
  const prefix = `Your ${label} climbed ${_fmtPct(Math.abs(top.delta))} last quarter`;
  return {
    prefix,
    headline: `${prefix} — ${DEFAULT_SUFFIX.gain}`,
    kind: 'gain',
    metricKey: top.key,
    deltaText: _fmtPct(Math.abs(top.delta)),
  };
}

export const _BCB_HERO_NARRATIVE_INTERNALS = { METRIC_LABELS, NUDGE_ELIGIBLE, DEFAULT_SUFFIX };
